import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import styled from "styled-components";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import GavelIcon from "@mui/icons-material/Gavel";
import GroupsIcon from "@mui/icons-material/Groups";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { usePallet } from "../../../contexts/PalletContext";
import ConsumoService, {
  IVolumetriaNovasProposicoesResponse,
} from "../../../services/ConsumoService";

// =====================================
// Cores das séries 
// =====================================
const COR_SENADO = "#4B1196";
const COR_CAMARA = "#7A38C9";
const COR_CONGRESSO = "#B084DE";
const COR_ESTADUAIS = "#F59E0B";

const MES_CURTO = [
  "Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
  "Jul", "Ago", "Set", "Out", "Nov", "Dez",
];

// =====================================
// Styled
// =====================================
const Container = styled(Grid)`
  padding: 20px 16px;
  background: #fafafa;
`;

const KPICard = styled(Paper)`
  padding: 16px;
  border-radius: 10px;
  display: flex;
  gap: 12px;
  align-items: center;
  height: 100%;
  box-sizing: border-box;
`;

const ChartCard = styled(Paper)`
  padding: 20px;
  border-radius: 10px;
  margin-top: 16px;
`;

function formatMesLabel(yyyyMM: string): string {
  const [ano, mes] = yyyyMM.split("-");
  const idx = Number(mes) - 1;
  return `${MES_CURTO[idx]}/${String(ano).slice(2)}`;
}

const GerUsuariosTabVolumetria = (_props: { id_user: number }) => {
  const { pallet } = usePallet();
  const corTexto = pallet?.textColorQuaternary || "#1E0736";

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<IVolumetriaNovasProposicoesResponse | undefined>(undefined);

  useEffect(() => {
    const hoje = new Date();
    const dtFin = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0); // fim do mês atual
    const dtIni = new Date(hoje.getFullYear(), hoje.getMonth() - 11, 1); // 12 meses atrás (início do mês)

    setLoading(true);
    ConsumoService.GetVolumetriaNovasProposicoes(dtIni, dtFin)
      .then((res) => setData(res))
      .catch((err) => console.error("Volumetria - erro:", err))
      .finally(() => setLoading(false));
  }, []);

  const chartData = useMemo(() => {
    const series = data?.series || [];
    return series.map((r) => ({
      mes: formatMesLabel(r.mes),
      Senado: r.senado,
      Câmara: r.camara,
      Congresso: r.congresso,
      "Estaduais / Municipais": r.estaduais_municipais,
    }));
  }, [data]);

  const totais = data?.totais || {
    senado: 0,
    camara: 0,
    congresso: 0,
    estaduais_municipais: 0,
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <Paper
        sx={{
          p: 1.2,
          background: pallet?.backgroundColorTertiary || "#F9F9F9",
          border: `1px solid ${pallet?.backgroungColorTertiaryBorder || "#cccccc"}`,
          fontFamily: pallet?.general?.fontFamily,
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: corTexto, mb: 0.5 }}>
          {label}
        </Typography>
        {payload.map((p: any) => (
          <Grid key={p.dataKey} container alignItems="center" sx={{ gap: 1 }}>
            <span
              style={{
                display: "inline-block",
                width: 8,
                height: 8,
                borderRadius: 2,
                background: p.color,
              }}
            />
            <Typography sx={{ fontSize: 12, color: "#444", flex: 1 }}>
              {p.dataKey}
            </Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: corTexto }}>
              {Number(p.value).toLocaleString("pt-BR")}
            </Typography>
          </Grid>
        ))}
      </Paper>
    );
  };

  const KPI = (props: {
    icon: React.ReactNode;
    label: string;
    value: number;
    color: string;
  }) => (
    <KPICard elevation={1}>
      <Avatar sx={{ bgcolor: props.color }}>{props.icon}</Avatar>
      <Grid>
        <Typography
          sx={{
            fontSize: 11,
            color: "#7a7a7a",
            textTransform: "uppercase",
            letterSpacing: 0.4,
            fontFamily: pallet?.general?.fontFamily,
          }}
        >
          {props.label}
        </Typography>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 22,
            color: corTexto,
            fontFamily: pallet?.general?.fontFamily,
          }}
        >
          {props.value.toLocaleString("pt-BR")}
        </Typography>
      </Grid>
    </KPICard>
  );

  return (
    <Container container>
      {/* KPIs */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <KPI icon={<AccountBalanceIcon />} label="Senado" value={totais.senado} color={COR_SENADO} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPI icon={<GavelIcon />} label="Câmara" value={totais.camara} color={COR_CAMARA} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPI icon={<GroupsIcon />} label="Congresso" value={totais.congresso} color={COR_CONGRESSO} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPI
            icon={<LocationCityIcon />}
            label="Estaduais / Municipais"
            value={totais.estaduais_municipais}
            color={COR_ESTADUAIS}
          />
        </Grid>
      </Grid>

      {/* GRÁFICO */}
      <ChartCard sx={{ width: "100%" }}>
        <Typography
          sx={{
            fontWeight: 700,
            color: corTexto,
            fontFamily: pallet?.general?.fontFamily,
            fontSize: 16,
            mb: 0.5,
          }}
        >
          Novas proposições · últimos 12 meses
        </Typography>
        <Typography
          sx={{
            fontSize: 12,
            color: "#7a7a7a",
            fontFamily: pallet?.general?.fontFamily,
            mb: 2,
          }}
        >
          Universo total · agrupado por casa
        </Typography>

        {loading ? (
          <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: 320 }}>
            <CircularProgress sx={{ color: pallet?.backgroundColorPrimary || "#3B0675" }} />
          </Grid>
        ) : chartData.length === 0 ? (
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            sx={{ minHeight: 320, border: "1px dashed #e0e0e0", borderRadius: 1 }}
          >
            <Typography sx={{ color: "#7a7a7a" }}>
              Sem dados para o período.
            </Typography>
          </Grid>
        ) : (
          <ResponsiveContainer width="100%" height={360}>
            <LineChart
              data={chartData}
              margin={{ top: 10, right: 24, left: 0, bottom: 5 }}
            >
              <CartesianGrid horizontal vertical={false} stroke="#eee" />
              <XAxis
                dataKey="mes"
                tick={{
                  fontFamily: pallet?.general?.fontFamily,
                  fontSize: 12,
                  fill: "#555",
                }}
              />
              <YAxis
                tick={{
                  fontFamily: pallet?.general?.fontFamily,
                  fontSize: 12,
                  fill: "#555",
                }}
                allowDecimals={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                iconType="square"
                wrapperStyle={{
                  fontFamily: pallet?.general?.fontFamily,
                  fontSize: 12,
                  paddingTop: 8,
                }}
              />
              <Line
                type="monotone"
                dataKey="Senado"
                stroke={COR_SENADO}
                strokeWidth={2}
                dot={{ fill: COR_SENADO, r: 3 }}
                activeDot={{ r: 5 }}
              />
              <Line
                type="monotone"
                dataKey="Câmara"
                stroke={COR_CAMARA}
                strokeWidth={2}
                dot={{ fill: COR_CAMARA, r: 3 }}
                activeDot={{ r: 5 }}
              />
              <Line
                type="monotone"
                dataKey="Congresso"
                stroke={COR_CONGRESSO}
                strokeWidth={2}
                dot={{ fill: COR_CONGRESSO, r: 3 }}
                activeDot={{ r: 5 }}
              />
              <Line
                type="monotone"
                dataKey="Estaduais / Municipais"
                stroke={COR_ESTADUAIS}
                strokeWidth={2}
                dot={{ fill: COR_ESTADUAIS, r: 3 }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </ChartCard>
    </Container>
  );
};

export default GerUsuariosTabVolumetria;
